import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Evaluation360Card } from './Evaluation360Card';
import { EvaluationPager, useEvaluationPager } from './EvaluationPager';
import { EVALUATION_PANEL_INNER_SURFACE, EvaluationPanel } from './EvaluationPanel';
import { EvaluationStartButton } from './EvaluationStartButton';
import { useStartEvaluation } from './useStartEvaluation';

import { useGetUsersByIds } from '@/api/directory';
import { useGetEvaluation360 } from '@/api/performance-profile';
import { EvaluationType } from '@/constants/cycle-evaluation/CycleEvaluation';
import { CycleInstanceSteps } from '@/constants/cycle-evaluation/CycleInstanceSteps';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';

interface Evaluation360PanelProps {
    employeeId?: string;
    isEmployeeInactive?: boolean;
    canStartEvaluations?: boolean;
    className?: string;
}

export const Evaluation360Panel = ({
    employeeId,
    isEmployeeInactive,
    canStartEvaluations,
    className,
}: Evaluation360PanelProps) => {
    const { t } = useTranslation();
    const { start: startEvaluation, isCreating } = useStartEvaluation({
        evaluationType: EvaluationType.EVALUATION_360,
        firstStep: CycleInstanceSteps.ASSIGN_REVIEWER,
    });

    const { data: evaluations, isLoading, error } = useGetEvaluation360(employeeId);

    const reviewerUserIds = useMemo(() => {
        if (!evaluations) return [];
        const ids = evaluations.flatMap((entry) => entry.reviewerUserIds);
        return Array.from(new Set(ids));
    }, [evaluations]);

    const { data: reviewerUsers } = useGetUsersByIds({
        userIds: reviewerUserIds,
        identifier: 'evaluation360-panel',
    });

    const { getManagerLabel } = useCompanyInformation();
    const { pageIndex, setPageIndex, pageCount, visible } = useEvaluationPager(evaluations, 3);

    return (
        <EvaluationPanel
            title={t('performanceProfile.evaluations.evaluation360.title')}
            description={t('performanceProfile.evaluations.evaluation360.description', {
                managerLabel: getManagerLabel(),
            })}
            className={className}
        >
            {isLoading ? (
                <div className={EVALUATION_PANEL_INNER_SURFACE}>
                    <div className={'text-obj-gray text-[14px]'}>
                        {t('performanceProfile.evaluations.loading')}
                    </div>
                </div>
            ) : error ? (
                <div className={EVALUATION_PANEL_INNER_SURFACE}>
                    <div className={'text-error text-[14px]'}>
                        {t('performanceProfile.evaluations.evaluation360.loadError')}
                    </div>
                </div>
            ) : !evaluations || evaluations.length === 0 ? (
                <div className={EVALUATION_PANEL_INNER_SURFACE}>
                    <div className={'text-obj-gray text-[14px]'}>
                        {t('performanceProfile.evaluations.evaluation360.noEvaluations')}
                    </div>
                </div>
            ) : (
                <div className={'flex flex-col gap-(--gap-gap-2xl)'}>
                    {visible.map((entry) => (
                        <Evaluation360Card
                            key={entry.reviewInstanceId}
                            entry={entry}
                            reviewerUsers={reviewerUsers}
                        />
                    ))}
                </div>
            )}

            <EvaluationPager
                pageIndex={pageIndex}
                pageCount={pageCount}
                onPickPage={setPageIndex}
            />

            {!isEmployeeInactive && (
                <EvaluationStartButton
                    canStartEvaluations={canStartEvaluations}
                    disabled={isCreating}
                    onClick={() => startEvaluation(employeeId)}
                    tooltip={t('performanceProfile.evaluations.disabledTooltip')}
                    iconName={'essential-send'}
                >
                    {t('performanceProfile.evaluations.evaluation360.startButton')}
                </EvaluationStartButton>
            )}
        </EvaluationPanel>
    );
};
